'use client';

/**
 * GlassCheckbox Component
 *
 * A checkbox with glow effects when checked.
 * Automatically adapts to light/dark theme.
 * Includes built-in security props for permission/tier/license locking.
 *
 * @see Issue #383 - Permission-Aware UI Components with Lock Icon Pattern
 * @see Issue #549 - Design System UI Compliance
 */

import LockIcon from '@mui/icons-material/Lock';
import Box from '@mui/material/Box';
import Checkbox, { type CheckboxProps } from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { alpha, styled, useTheme } from '@mui/material/styles';
import type { SecurityProps } from '@ops-matrix/shared-types';
import React, { forwardRef, useCallback } from 'react';
import { useSecurityLock } from '../../hooks/useSecurityLock';
import { getTierLabel } from '../../providers/PermissionProvider';
import { transitionPresets } from '../../theme/transitions';
import { iconSizes } from '../../theme/typography';
import { transientOptions } from '../../utils/styledUtils';
import { GlassChip } from '../GlassChip/GlassChip';
import { GlassTooltip } from '../GlassTooltip';

// ============================================================================
// Types
// ============================================================================

export interface GlassCheckboxProps extends Omit<CheckboxProps, 'onChange'>, SecurityProps {
  /**
   * Label text
   */
  label?: React.ReactNode;

  /**
   * Label placement
   * @default 'end'
   */
  labelPlacement?: 'end' | 'start' | 'top' | 'bottom';

  /**
   * Enable glow effect when checked
   * @default true
   */
  glowWhenChecked?: boolean;

  /**
   * Glow color variant
   * @default 'primary'
   */
  glowColor?: 'primary' | 'secondary' | 'mixed';

  /**
   * Change handler
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
}

// ============================================================================
// Styled Components
// ============================================================================

interface StyledCheckboxProps {
  $isDark: boolean;
  $glowWhenChecked: boolean;
  $glowColor: 'primary' | 'secondary' | 'mixed';
  $checkboxColor: CheckboxProps['color'];
}

const StyledCheckbox = styled(
  Checkbox,
  transientOptions,
)<StyledCheckboxProps>(({ theme, $isDark, $glowWhenChecked, $glowColor: _glowColor, $checkboxColor }) => {
  const colorKey = $checkboxColor === 'default' ? 'primary' : ($checkboxColor ?? 'primary');
  const mainColor = theme.palette[colorKey]?.main ?? theme.palette.primary.main;

  return {
    padding: 8,
    transition: transitionPresets.button,
    borderRadius: 8,

    '&:hover': {
      // ── Glass hover — frosted glass materialises ──
      backgroundColor: $isDark ? 'rgba(255,255,255,0.07)' : 'rgba(255,255,255,0.50)',
      backdropFilter: 'blur(16px) saturate(150%)',
      WebkitBackdropFilter: 'blur(16px) saturate(150%)',
    },

    '&.Mui-checked': {
      color: mainColor,
      ...($glowWhenChecked && {
        // ── Checked glow — accent-colored glow behind icon ──
        backgroundColor: alpha(mainColor, $isDark ? 0.08 : 0.06),
        '& .MuiSvgIcon-root': {
          filter: `drop-shadow(0 0 6px ${alpha(mainColor, 0.45)})`,
        },
      }),
    },

    '&.Mui-disabled': {
      opacity: 0.5,
    },

    '& .MuiSvgIcon-root': {
      fontSize: 22, // Off-scale: between base (20) and lg (24)
      borderRadius: 6,
      transition: transitionPresets.button,
    },

    // Indeterminate state styling
    '&.MuiCheckbox-indeterminate': {
      color: mainColor,
      ...($glowWhenChecked && {
        backgroundColor: alpha(mainColor, $isDark ? 0.08 : 0.06),
        '& .MuiSvgIcon-root': {
          filter: `drop-shadow(0 0 6px ${alpha(mainColor, 0.45)})`,
        },
      }),
    },
  };
});

// ============================================================================
// Component
// ============================================================================

export const GlassCheckbox = forwardRef<HTMLButtonElement, GlassCheckboxProps>(
  (
    {
      label,
      labelPlacement = 'end',
      glowWhenChecked = true,
      glowColor = 'primary',
      size = 'medium',
      color = 'primary',
      disabled,
      onChange,
      // Security props
      permission,
      anyPermission,
      allPermissions,
      requiredTier,
      requiredLicense,
      anyLicense,
      showLockIcon = true,
      showTierBadge = true,
      onLockedClick,
      lockedTooltip,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // Security lock state
    const { lockState, isLocked, handleLockedClick, lockedTooltipText } = useSecurityLock({
      permission,
      anyPermission,
      allPermissions,
      requiredTier,
      requiredLicense,
      anyLicense,
      onLockedClick,
      lockedTooltip,
    });

    const isDisabled = disabled || isLocked;

    // Handle change with security interception
    const handleChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        if (isLocked) {
          e.preventDefault();
          e.stopPropagation();
          handleLockedClick();
          return;
        }
        onChange?.(e, checked);
      },
      [isLocked, handleLockedClick, onChange],
    );

    // Handle click on the locked area
    const handleLockedAreaClick = useCallback(() => {
      if (isLocked) {
        handleLockedClick();
      }
    }, [isLocked, handleLockedClick]);

    // Lock indicator
    const lockIndicator = isLocked && showLockIcon && lockState && (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          ml: labelPlacement === 'end' ? 1 : 0,
          mr: labelPlacement === 'start' ? 1 : 0,
        }}
      >
        <LockIcon
          sx={{
            fontSize: iconSizes.sm,
            color:
              lockState.type === 'tier'
                ? theme.palette.warning.main
                : lockState.type === 'license'
                  ? theme.palette.info.main
                  : theme.palette.action.disabled,
          }}
        />
        {showTierBadge && lockState.type === 'tier' && lockState.requiredTier && (
          <GlassChip
            label={getTierLabel(lockState.requiredTier)}
            size="small"
            color="warning"
            sx={{
              height: 18,
              fontSize: '0.75rem',
              '& .MuiChip-label': { px: 0.5 },
            }}
          />
        )}
      </Box>
    );

    const checkboxElement = (
      <StyledCheckbox
        ref={ref}
        color={color}
        size={size}
        $isDark={isDark}
        $glowWhenChecked={glowWhenChecked}
        $glowColor={glowColor}
        $checkboxColor={color}
        disabled={isDisabled}
        onChange={handleChange}
        {...props}
      />
    );

    // Build the full component
    const content = label ? (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: isLocked ? 'pointer' : 'default',
          opacity: isLocked ? 0.7 : 1,
        }}
        onClick={isLocked ? handleLockedAreaClick : undefined}
      >
        <FormControlLabel
          control={checkboxElement}
          label={
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <span>{label}</span>
              {lockIndicator}
            </Box>
          }
          labelPlacement={labelPlacement}
          sx={{
            marginLeft: 0,
            marginRight: 0,
            '& .MuiFormControlLabel-label': {
              fontSize: size === 'small' ? '0.8125rem' : '0.9375rem',
              color: isDisabled ? 'text.disabled' : 'text.primary',
            },
          }}
        />
      </Box>
    ) : (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          cursor: isLocked ? 'pointer' : 'default',
        }}
        onClick={isLocked ? handleLockedAreaClick : undefined}
      >
        {checkboxElement}
        {lockIndicator}
      </Box>
    );

    // Wrap with tooltip if locked
    if (isLocked) {
      return (
        <GlassTooltip title={lockedTooltipText} placement="top">
          {content}
        </GlassTooltip>
      );
    }

    return content;
  },
);

GlassCheckbox.displayName = 'GlassCheckbox';

export default GlassCheckbox;
